import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom';

import {
  useQuery,
  useMutation,
  useQueryClient
} from '@tanstack/react-query';

import useAuth from '../../../../auth/useAuth';
import AuthService from '../../../../auth/AuthService';
import { useTour } from '../../../../context/TourContext';
import { updateUserOnboarding } from '../../../Login/api';

import SubPagesLayout from '../../../UI/layout/subPages';
import AssignmentsList from '../../../UI/assignments';
import InstructorAddAssignmentsRow from '../../../UI/instructor/settings/assignments/addRow';

import {
  generalInfoAssignmentQuery,
  deleteGame,
  deleteTournament
} from './api';

import { navigation } from './data';

export default function InstructorSettingsAssignments() {
  const { getUser } = useAuth();
  const location = useLocation();
  const { startTour, isActive } = useTour();

  const user = getUser();

  // Shared launcher for the assignment-creation tour: used by both the
  // initial onboarding flow (when redirected here from All Exercises with
  // location.state.startTour) and the Guide Me button on this page. The
  // onComplete handler PATCHes onboarded:true if the user hasn't been
  // marked onboarded yet — runs once and is a no-op on subsequent calls.
  const startAssignmentCreationTour = useCallback(() => {
    startTour('assignment-creation', {
      onComplete: async () => {
        const current = AuthService.getUser();
        if (current?.onboarded === true) return;
        try {
          await updateUserOnboarding({ id: user.id });
          AuthService.setUser({ ...current, onboarded: true });
        } catch (err) {
          console.error('Failed to mark onboarding complete:', err);
        }
      },
    });
  }, [startTour, user?.id]);

  useEffect(() => {
    if (location.state?.startTour && !isActive && !user?.onboarded) {
      // Clear the navigation state so refreshing doesn't re-trigger
      window.history.replaceState({}, '');
      startAssignmentCreationTour();
    }
  }, [location.state, isActive, user?.onboarded, startAssignmentCreationTour]);

  const [isLaunchReviewSessionButtonDisabled, setIsLaunchReviewSessionButtonDisabled] = useState(false);
  const queryClient = useQueryClient();
  const { data: generalInfo, refetch: refetchGeneralInfo } = useQuery(generalInfoAssignmentQuery());

  // Invalidate every `['generalInfo', ...]` cache variant after a
  // game/tournament delete. Without this, only the slim ?assignment
  // query used on this page gets refreshed; the full generalInfoQuery
  // used by the Add page keeps returning the pre-delete game list from
  // its 5-minute cache, so the activity buttons stay stuck as "Limit
  // reached!" until the cache expires or the user hard-refreshes.
  const invalidateGeneralInfoFamily = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['generalInfo'] });
  }, [queryClient]);

  const games = generalInfo?.games;
  const courses = generalInfo?.courses;
  const sessions = generalInfo?.sessions;
  const tournaments = generalInfo?.tournaments;

  // Refresh-resume guard: if the user refreshed while paused on Step 1
  // of the assignment-creation tour and still hasn't created any real
  // assignments (only the seeded Demo Assignment exists), restart the
  // tour. Any other tour-state in sessionStorage is treated as stale and
  // wiped so it doesn't accumulate. Gated on games being loaded so the
  // demo-only check is reliable.
  useEffect(() => {
    if (isActive || user?.onboarded || !games) return;

    const wasActive = sessionStorage.getItem('onboardingTourActive') === 'true';
    if (!wasActive) return;

    const wasName = sessionStorage.getItem('onboardingTourName');
    const wasStep = sessionStorage.getItem('onboardingTourStep');
    const onlyDemoExists = !games.some(g => g.name !== 'Demo Assignment');

    if (wasName === 'assignment-creation' && wasStep === '0' && onlyDemoExists) {
      startAssignmentCreationTour();
    } else {
      sessionStorage.removeItem('onboardingTourActive');
      sessionStorage.removeItem('onboardingTourStep');
      sessionStorage.removeItem('onboardingTourName');
      sessionStorage.removeItem('onboardingTourGameId');
    }
  }, [games, isActive, user?.onboarded, startAssignmentCreationTour]);

  useEffect(() => {
    if(courses?.length && tournaments && generalInfo?.users) {
      const currentCourse = courses[0];

      // Treat null game_limit as 1 — the field comes back null on
      // courses where the cap was never explicitly configured, but
      // null coerces to 0 in numeric comparison and would disable the
      // button even at zero tournaments. Defaulting to 1 lets these
      // courses launch one Review Session without a backend change.
      const gameLimit = currentCourse.game_limit ?? 1;

      if(tournaments.length >= gameLimit) {
        setIsLaunchReviewSessionButtonDisabled(true);
      } else {
        setIsLaunchReviewSessionButtonDisabled(false);
      }
    }
  }, [courses, tournaments, generalInfo?.users]);

  const deleteTournamentMutation = useMutation({
    mutationFn: (data) => deleteTournament(data)
  });

  const deleteGameMutation = useMutation({
    mutationFn: (data) => deleteGame(data)
  });

  const handleTournamentDelete = async (tournamentId, tournamentDeletedCallback) => {
    deleteTournamentMutation.mutate(
      { tournamentId },
      {
        onSuccess: () => {
          tournamentDeletedCallback();
          refetchGeneralInfo();
          invalidateGeneralInfoFamily();
        },
      }
    );
  };

  const handleGameDelete = async (gameId, gameDeletedCallback) => {
    deleteGameMutation.mutate(
      { gameId },
      {
        onSuccess: () => {
          gameDeletedCallback();
          refetchGeneralInfo();
          invalidateGeneralInfoFamily();
        },
      }
    );
  };

  const nonTournamentGames = useMemo(() => {
    if (!games) return [];
    return games.filter(game => !game.tournament_ids?.length);
  }, [games]);

  return (
    <SubPagesLayout
      navigation={navigation}
      contentClassName="adjustment-content-container"
    >
      <InstructorAddAssignmentsRow
        isLaunchReviewSessionButtonDisabled={isLaunchReviewSessionButtonDisabled}
      />
      <AssignmentsList
        user={user}
        games={games}
        nonTournamentGames={nonTournamentGames}
        tournaments={tournaments}
        sessions={sessions}
        onTournamentDeletionConfirm={handleTournamentDelete}
        onGameDeletionConfirm={handleGameDelete}
      />
    </SubPagesLayout>
  );
}
